/**
 * Team display names — ONE rule for every sport (HYDRA_API.md "Team names —
 * `canonicalTeamNames`, all sports"):
 *
 *     displayName(gameKey, code) = namesByGame[gameKey]?.[code] ?? code
 *
 * Hydra resolves the name per sport (franchise tables, Poly club names, the
 * Kalshi leg's own subtitle, the 1v1 minter's player names) and stamps the
 * game's `{code: name}` map on EVERY row that carries `canonicalGameKeyV2` —
 * Kalshi, Polymarket and Poly US, every class, props and ALT rows included.
 * g1 holds no per-sport naming data: no code table, no subtitle read, no ticker
 * parse, no sport or league list decides what a team is called.
 *
 * The index holds one map PER PRODUCER per game (#1478). A producer is the
 * row's `source` — kalshi, polymarket, polymarket_us — and its entry is the map
 * from ITS latest keyed row of that game, replaced wholesale, INCLUDING when
 * that row carries no map: a producer that stops naming a side surfaces as the
 * code for that producer, never masked by a value it used to send (the #685
 * index discipline, now per producer). Within one producer hydra's per-game
 * invariant still holds — every keyed row of a game carries the identical map —
 * so a prop or ALT row cannot clear names a game row set, and arrival order
 * does not matter.
 *
 * Across producers it does not hold: on node1 the Poly US rows come from the
 * hepu edges (hc1's directory) and carry names, while the Kalshi and Poly intl
 * rows for the same game come from the StreamRift fleet and carry none. With a
 * single last-row-wins map the board flipped between name and code on every
 * interleaved frame (Dan, 2026-11-04). So the lookup shows a named entry from
 * whichever producer named the side most recently, and the code only when NO
 * producer names it. The trade, stated: a producer that stops naming a side is
 * masked while another producer still names it. One rule for every venue — no
 * Poly US branch, no venue preference, only "named beats unnamed".
 */
import { foldGameKeySport, parseGameKeyV2, promoteToGameKeyV2 } from '../services/analyticsGameKey';

/** Who sent the row: the frame's `source`. Unknown sources share one bucket. */
export type TeamNamesProducer = string;

interface GameNames {
  /** Each producer's latest map for this game. Absent = that producer names nothing. */
  byProducer: Map<TeamNamesProducer, ReadonlyMap<string, string>>;
  /** Producers that currently name something, most recently named first. */
  order: TeamNamesProducer[];
}

const _namesByGame = new Map<string, GameNames>();

/** The index's key for any spelling of a game key: the promoted 5-seg form with
 *  the sport segment folded (`ncaaf|…` and Poly US's `cfb|…` are one game).
 *  Null for anything that is not a canonical game key (event tickers, synthetic
 *  panel keys). */
function namesKey(gameKey: string | null | undefined): string | null {
  const parts = parseGameKeyV2(gameKey);
  if (!parts?.sport) return null;
  return foldGameKeySport(promoteToGameKeyV2(gameKey));
}

/** The wire map, validated against the key it rides on: keys are upper-cased
 *  and must be one of the key's two codes (the contract asserts it; a stray key
 *  must not name a code it has no authority over), values non-empty trimmed
 *  strings. Null when nothing valid remains — the "absent" state. */
function namesFromWire(gameKey: string, wire: unknown): ReadonlyMap<string, string> | null {
  if (!wire || typeof wire !== 'object' || Array.isArray(wire)) return null;
  const parts = parseGameKeyV2(gameKey);
  if (!parts) return null;
  const names = new Map<string, string>();
  for (const [code, name] of Object.entries(wire as Record<string, unknown>)) {
    const c = code.trim().toUpperCase();
    if (c !== parts.teamLo && c !== parts.teamHi) continue;
    if (typeof name !== 'string' || !name.trim()) continue;
    names.set(c, name.trim());
  }
  return names.size > 0 ? names : null;
}

/** Record what one keyed row says its game's sides are called. Called for every
 *  admitted frame that carries `canonicalGameKeyV2`; the row's map REPLACES its
 *  producer's entry for the game, and a row without one clears that producer's
 *  entry (other producers' entries are untouched). A row with no v2 key has no
 *  authority over any game and must not be passed here. */
export function recordGameTeamNames(gameKey: string | null | undefined, wire: unknown, producer: TeamNamesProducer): void {
  const key = namesKey(gameKey);
  if (!key) return;
  const names = namesFromWire(key, wire);
  const game = _namesByGame.get(key);
  if (names) {
    const entry = game ?? { byProducer: new Map(), order: [] };
    entry.byProducer.set(producer, names);
    entry.order = [producer, ...entry.order.filter((p) => p !== producer)];
    if (!game) _namesByGame.set(key, entry);
    return;
  }
  if (!game) return;
  game.byProducer.delete(producer);
  game.order = game.order.filter((p) => p !== producer);
  if (game.order.length === 0) _namesByGame.delete(key);
}

/** Hydra's name for one side of a game, or null when no producer has named it.
 *  For callers that need to know whether a label is named (a highlight token). */
export function gameTeamName(gameKey: string | null | undefined, code: string | null | undefined): string | null {
  if (!code) return null;
  const key = namesKey(gameKey);
  if (!key) return null;
  const game = _namesByGame.get(key);
  if (!game) return null;
  const c = code.trim().toUpperCase();
  for (const producer of game.order) {
    const name = game.byProducer.get(producer)?.get(c);
    if (name) return name;
  }
  return null;
}

/** THE display rule: hydra's name for the side, else the code itself. */
export function teamDisplayName(gameKey: string | null | undefined, code: string): string {
  return gameTeamName(gameKey, code) ?? code;
}

/** Test seam: forget every game, as a page load does. */
export function _resetGameTeamNamesForTests(): void {
  _namesByGame.clear();
}
